import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Box, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import logger from 'lib/logger';
import { useAuth } from 'lib/useAuth';
import { IOrderReadResourceV18 } from 'components/order/model/Order';
import { orderApi } from 'components/order/orderApi';
import { ActionListSkeleton } from 'components/activity/ui/List/ActionListSkeleton';
import { OrdersList } from 'components/activity/ui/List/OrdersList';
import { getAllLocations } from 'store/locations/locationActions';

const StyledEmptyBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2)
}));

export const OrdersTab: React.FC = () => {
    const dispatch = useDispatch();
    const { user } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = React.useState(true);
    const [orders, setOrders] = React.useState<IOrderReadResourceV18[]>([]);

    React.useLayoutEffect(() => {
        // Scroll to the top of the page
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    React.useEffect(() => {
        const loadOrders = async () => {
            if (user?.id) {
                setLoading(true);
                try {
                    const response = await orderApi.getOrders({});
                    setOrders(items => [...(items ?? []), ...response.items]);
                    setLoading(false);
                } catch (error: any) {
                    logger.error(error.message, error);
                }
            }
        };
        loadOrders();
    }, [user?.id]);

    React.useEffect(() => {
        getAllLocations()(dispatch);
    }, [dispatch]);

    const renderEmptyState = React.useCallback(
        () => (
            <StyledEmptyBox>
                <Typography variant="body1">{t('ACTIVITY_ORDERS_LIST_EMPTY')}</Typography>
                <Typography variant="body2">{t('ACTIVITY_ORDERS_LIST_EMPTY_DESCRIPTION')}</Typography>
            </StyledEmptyBox>
        ),
        [t]
    );

    const isEmpty = React.useMemo(() => !orders.length && !loading, [orders, loading]);

    return (
        <Box sx={{ height: '100%' }}>
            {isEmpty && renderEmptyState()}
            {orders.length > 0 && <OrdersList list={orders} />}
            {loading && <ActionListSkeleton showIcon={false} />}
        </Box>
    );
};
